import { db } from '../firebase/config';
import { collection, addDoc, updateDoc, doc, getDoc, query, where, getDocs, deleteDoc } from 'firebase/firestore';
import { DomainConfig } from '../types';

export class DomainService {
  private static readonly COLLECTION_NAME = 'domains';
  private static readonly API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  private static normalizeDomainInput(domain: string): string {
    const trimmed = domain.trim();
    if (!trimmed) {
      return '';
    }

    return trimmed
      .replace(/^[a-z][a-z0-9+.-]*:\/\//i, '')
      .split(/[/?#]/)[0]
      .replace(/:\d+$/, '')
      .replace(/\.+$/, '')
      .toLowerCase();
  }

  /**
   * Cloudflare Zoneを作成してドメインを設定
   * @param siteId サイトID
   * @param domain 独自ドメイン（例: example.com）
   * @param basePath パスプレフィックス（例: /campaign1）。空文字列または未設定の場合はルートパス
   * @param embedMode 設定方式（'direct' | 'embed'）。デフォルト: 'direct'
   * @returns DomainConfig
   */
  static async createDomain(siteId: string, domain: string, basePath?: string, embedMode?: 'direct' | 'embed'): Promise<DomainConfig> {
    try {
      const normalizedDomain = this.normalizeDomainInput(domain);
      if (!normalizedDomain) {
        throw new Error('ドメインを入力してください');
      }

      const existingDomainsSnapshot = await getDocs(
        query(
          collection(db, this.COLLECTION_NAME),
          where("domain", "==", normalizedDomain),
          where("isActive", "==", true)
        )
      );

      const mode = embedMode || 'direct';
      
      let zoneId: string;
      let nameservers: string[];
      let status: 'pending' | 'active' | 'initializing' | 'moved' | 'deleted' | 'deactivated' | 'unknown';
      let message: string | undefined;

      // 埋め込み方式の場合、Cloudflare Zoneは作成しない
      if (mode === 'embed') {
        zoneId = ''; // 埋め込み方式ではZone IDは不要
        nameservers = [];
        status = 'active';
        message = '埋め込み方式で設定されました';
      } else if (!existingDomainsSnapshot.empty) {
        const existingDomain = existingDomainsSnapshot.docs[0].data() as DomainConfig;
        zoneId = existingDomain.zoneId;
        nameservers = existingDomain.nameservers;
        status = existingDomain.status;
        message = existingDomain.statusMessage;
      } else {
        const response = await fetch(`${this.API_BASE_URL}/api/create-zone`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            domain: normalizedDomain,
            siteId,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Zone作成に失敗しました');
        }

        const result = await response.json();
        
        if (!result.success) {
          throw new Error(result.error || 'Zone作成に失敗しました');
        }

        zoneId = result.zoneId;
        nameservers = result.nameservers || [];
        status = result.status || 'pending';
        message = result.message;
      }

      const normalizedBasePath = basePath 
        ? (basePath.startsWith('/') ? basePath : `/${basePath}`).replace(/\/$/, '') || undefined
        : undefined;

      const domainData: Partial<DomainConfig> = {
        zoneId,
        domain: normalizedDomain,
        basePath: normalizedBasePath,
        nameservers,
        status,
        statusMessage: message,
        isActive: mode === 'embed' ? true : status === 'active', // 埋め込み方式の場合は常にtrue
        createdAt: new Date(),
        lastChecked: new Date(),
        embedMode: mode,
      };

      // undefinedフィールドを除外（Firestoreはundefinedを許可しない）
      const cleanedDomainData: any = {};
      Object.keys(domainData).forEach((key) => {
        const value = (domainData as any)[key];
        if (value !== undefined) {
          cleanedDomainData[key] = value;
        }
      });

      const docRef = await addDoc(collection(db, this.COLLECTION_NAME), {
        ...cleanedDomainData,
        siteId,
      });

      return {
        ...domainData,
        zoneId,
        domain: normalizedDomain,
        basePath: normalizedBasePath,
        nameservers,
        status,
        isActive: mode === 'embed' ? true : status === 'active',
        embedMode: mode,
        createdAt: new Date(),
      } as DomainConfig;
    } catch (error) {
      console.error('ドメイン作成エラー:', error);
      throw error;
    }
  }

  /**
   * ドメインのステータスを検証（ネームサーバー変更確認）
   * @param siteId サイトID
   * @returns DomainConfig
   */
  static async verifyDomain(siteId: string): Promise<DomainConfig> {
    try {
      // Firestoreからドメイン設定を取得
      const domainConfig = await this.getDomain(siteId);
      
      if (!domainConfig) {
        throw new Error('ドメイン設定が見つかりません');
      }

      // Cloudflare APIでZoneステータスを確認
      const response = await fetch(`${this.API_BASE_URL}/api/verify-zone`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          zoneId: domainConfig.zoneId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Zone検証に失敗しました');
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Zone検証に失敗しました');
      }

      // Firestoreのドメイン設定を更新
      const updatedData: any = {
        status: result.status,
        statusMessage: result.statusMessage,
        isActive: result.isActive,
        nameservers: result.nameservers || domainConfig.nameservers,
        lastChecked: new Date(),
        dnsRecordsCount: result.dnsRecordsCount,
      };

      // undefinedを避けるため、値が存在する場合のみ追加
      if (result.originalNameServers) {
        updatedData.originalNameServers = result.originalNameServers;
      }
      if (result.modifiedOn) {
        updatedData.modifiedAt = new Date(result.modifiedOn);
      }
      if (result.activatedOn) {
        updatedData.activatedAt = new Date(result.activatedOn);
      }

      await this.updateDomain(siteId, updatedData);

      return {
        ...domainConfig,
        ...updatedData,
      } as DomainConfig;
    } catch (error) {
      console.error('ドメイン検証エラー:', error);
      throw error;
    }
  }

  /**
   * ドメイン設定を取得
   * @param siteId サイトID
   * @returns DomainConfig | null
   */
  static async getDomain(siteId: string): Promise<DomainConfig | null> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('siteId', '==', siteId)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }

      const docData = querySnapshot.docs[0];
      const data = docData.data();
      
      return {
        zoneId: data.zoneId,
        domain: data.domain,
        basePath: data.basePath,
        nameservers: data.nameservers || [],
        originalNameServers: data.originalNameServers,
        status: data.status,
        statusMessage: data.statusMessage,
        isActive: data.isActive || false,
        createdAt: data.createdAt?.toDate() || new Date(),
        modifiedAt: data.modifiedAt?.toDate(),
        activatedAt: data.activatedAt?.toDate(),
        lastChecked: data.lastChecked?.toDate(),
        dnsRecordsCount: data.dnsRecordsCount,
        embedMode: data.embedMode,
      };
    } catch (error) {
      console.error('ドメイン取得エラー:', error);
      throw error;
    }
  }

  /**
   * ドメイン名からドメイン設定とサイトIDを取得
   * @param domainName ドメイン名（例: example.com）
   * @returns { domainConfig: DomainConfig, siteId: string } | null
   */
  static async getDomainByName(domainName: string): Promise<{ domainConfig: DomainConfig; siteId: string } | null> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('domain', '==', domainName),
        where('isActive', '==', true)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }

      const docData = querySnapshot.docs[0];
      const data = docData.data();
      
      const domainConfig: DomainConfig = {
        zoneId: data.zoneId,
        domain: data.domain,
        basePath: data.basePath,
        nameservers: data.nameservers || [],
        originalNameServers: data.originalNameServers,
        status: data.status,
        statusMessage: data.statusMessage,
        isActive: data.isActive || false,
        createdAt: data.createdAt?.toDate() || new Date(),
        modifiedAt: data.modifiedAt?.toDate(),
        activatedAt: data.activatedAt?.toDate(),
        lastChecked: data.lastChecked?.toDate(),
        dnsRecordsCount: data.dnsRecordsCount,
        embedMode: data.embedMode,
      };
      
      return {
        domainConfig,
        siteId: data.siteId,
      };
    } catch (error) {
      console.error('ドメイン名検索エラー:', error);
      throw error;
    }
  }

  /**
   * ドメイン名とパスからドメイン設定とサイトIDを取得（パスベースのルーティング対応）
   * @param domainName ドメイン名（例: example.com）
   * @param pathname パス名（例: /campaign1）
   * @returns { domainConfig: DomainConfig, siteId: string } | null
   */
  static async getDomainByNameAndPath(domainName: string, pathname: string): Promise<{ domainConfig: DomainConfig; siteId: string } | null> {
    try {
      // パスを正規化（先頭に/を追加、末尾の/を削除）
      const normalizedPath = pathname.startsWith('/') ? pathname : `/${pathname}`;
      const cleanPath = normalizedPath.replace(/\/$/, '') || '/';

      // 該当ドメインのすべてのアクティブな設定を取得
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('domain', '==', domainName),
        where('isActive', '==', true)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }

      // パスに一致する設定を探す
      // 1. 完全一致するbasePathを持つ設定を優先
      // 2. basePathが空または未設定（ルートパス）の設定をフォールバック
      let matchedDoc: any = null;
      let rootDoc: any = null;

      for (const doc of querySnapshot.docs) {
        const data = doc.data();
        const basePath = data.basePath || '';
        const normalizedBasePath = basePath.startsWith('/') ? basePath : (basePath ? `/${basePath}` : '');
        const cleanBasePath = normalizedBasePath.replace(/\/$/, '') || '';

        // ルートパスの設定を保存
        if (!cleanBasePath || cleanBasePath === '/') {
          rootDoc = { doc, data };
        }

        // パスがbasePathと一致する場合
        if (cleanPath === cleanBasePath || (cleanBasePath && cleanPath.startsWith(cleanBasePath + '/'))) {
          matchedDoc = { doc, data };
          break; // 最初に一致したものを使用
        }
      }

      // 一致する設定が見つからない場合は、ルートパスの設定を返す
      const targetDoc = matchedDoc || rootDoc;
      
      if (!targetDoc) {
        return null;
      }

      const data = targetDoc.data;
      
      const domainConfig: DomainConfig = {
        zoneId: data.zoneId,
        domain: data.domain,
        basePath: data.basePath,
        nameservers: data.nameservers || [],
        originalNameServers: data.originalNameServers,
        status: data.status,
        statusMessage: data.statusMessage,
        isActive: data.isActive || false,
        createdAt: data.createdAt?.toDate() || new Date(),
        modifiedAt: data.modifiedAt?.toDate(),
        activatedAt: data.activatedAt?.toDate(),
        lastChecked: data.lastChecked?.toDate(),
        dnsRecordsCount: data.dnsRecordsCount,
        embedMode: data.embedMode,
      };
      
      return {
        domainConfig,
        siteId: data.siteId,
      };
    } catch (error) {
      console.error('ドメイン名・パス検索エラー:', error);
      throw error;
    }
  }

  /**
   * ドメイン設定を更新
   * @param siteId サイトID
   * @param updates 更新内容
   */
  static async updateDomain(siteId: string, updates: Partial<DomainConfig>): Promise<void> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('siteId', '==', siteId)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        throw new Error('ドメイン設定が見つかりません');
      }

      // undefinedフィールドを除外
      const cleanedUpdates: any = {};
      Object.keys(updates).forEach((key) => {
        const value = (updates as any)[key];
        if (value !== undefined) {
          cleanedUpdates[key] = value;
        }
      });

      const docRef = doc(db, this.COLLECTION_NAME, querySnapshot.docs[0].id);
      await updateDoc(docRef, {
        ...cleanedUpdates,
        lastChecked: new Date(),
      });
    } catch (error) {
      console.error('ドメイン更新エラー:', error);
      throw error;
    }
  }

  /**
   * ドメイン設定を削除（Cloudflare Zoneも削除）
   * @param siteId サイトID
   */
  static async deleteDomain(siteId: string): Promise<void> {
    try {
      const domainConfig = await this.getDomain(siteId);
      
      if (!domainConfig) {
        return; // 既に削除されている
      }

      // Cloudflare APIでZoneを削除
      const response = await fetch(`${this.API_BASE_URL}/api/delete-zone`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          zoneId: domainConfig.zoneId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Zone削除エラー:', errorData.error);
        // エラーでもFirestoreからは削除を続行
      }
      
      // Firestoreから削除
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('siteId', '==', siteId)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const docRef = doc(db, this.COLLECTION_NAME, querySnapshot.docs[0].id);
        await deleteDoc(docRef);
      }
    } catch (error) {
      console.error('ドメイン削除エラー:', error);
      throw error;
    }
  }

  /**
   * DNSレコードを確認
   * @param zoneId Zone ID
   * @param domain ドメイン名
   * @returns DNS情報
   */
  static async checkDns(zoneId: string, domain: string): Promise<any> {
    try {
      const response = await fetch(`${this.API_BASE_URL}/api/check-dns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          zoneId,
          domain,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'DNS確認に失敗しました');
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'DNS確認に失敗しました');
      }

      return result;
    } catch (error) {
      console.error('DNS確認エラー:', error);
      throw error;
    }
  }

  /**
   * DNSレコードを修正（Firebase Hostingへ）
   * @param zoneId Zone ID
   * @param domain ドメイン名
   * @returns 結果
   */
  static async fixDns(zoneId: string, domain: string): Promise<any> {
    try {
      const response = await fetch(`${this.API_BASE_URL}/api/fix-dns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          zoneId,
          domain,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'DNS修正に失敗しました');
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'DNS修正に失敗しました');
      }

      return result;
    } catch (error) {
      console.error('DNS修正エラー:', error);
      throw error;
    }
  }

  /**
   * ドメインの表示用URLを生成
   * @param domain ドメイン名
   * @returns 表示用URL
   */
  static getDisplayUrl(domain: string): string {
    return `https://${domain}`;
  }

  /**
   * ドメインのステータスを日本語で取得
   * @param status ステータス
   * @returns 日本語のステータス
   */
  static getStatusText(status: DomainConfig['status']): string {
    const statusMap: Record<DomainConfig['status'], string> = {
      pending: '設定待ち',
      active: '有効',
      initializing: '初期化中',
      moved: '移動済み',
      deleted: '削除済み',
      deactivated: '無効',
      unknown: '不明',
    };
    return statusMap[status] || '不明';
  }

  /**
   * ドメインのステータスカラーを取得
   * @param status ステータス
   * @returns MUIのカラー名
   */
  static getStatusColor(status: DomainConfig['status']): 'success' | 'warning' | 'error' | 'info' | 'default' {
    const colorMap: Record<DomainConfig['status'], 'success' | 'warning' | 'error' | 'info' | 'default'> = {
      active: 'success',
      pending: 'warning',
      initializing: 'info',
      moved: 'warning',
      deleted: 'error',
      deactivated: 'error',
      unknown: 'default',
    };
    return colorMap[status] || 'default';
  }

  /**
   * Cloudflareキャッシュをパージ
   * @param zoneId Zone ID
   */
  static async purgeCache(zoneId: string): Promise<void> {
    try {
      const response = await fetch(`${this.API_BASE_URL}/api/purge-cache`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          zoneId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'キャッシュパージに失敗しました');
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'キャッシュパージに失敗しました');
      }
    } catch (error) {
      console.error('キャッシュパージエラー:', error);
      throw error;
    }
  }
}
